/**
 * Billing API 훅 (크레딧 & 플랜 & 결제)
 *
 * 백엔드 billing 앱의 /api/billing/ 엔드포인트와 연동.
 * 1 크레딧 = ₩10
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AXIOS_INSTANCE } from '@/lib/api/orval/custom-instance';

// ============================================================================
// Types
// ============================================================================

export interface CreditWallet {
  id: string;
  balance: number;
  totalEarned: number;
  totalSpent: number;
  updatedAt: string;
}

export interface Plan {
  id: string;
  tier: 'free' | 'basic' | 'pro';
  name: string;
  description: string;
  signupBonusCredits: number;
  monthlyFreeCredits: number;
  priceMonthly: number;
  priceYearly: number;
  maxChannels: number;
  maxConcurrentGenerations: number;
  sortOrder: number;
}

export interface Subscription {
  id: string;
  plan: Plan;
  status: 'active' | 'expired' | 'cancelled' | 'trial';
  startedAt: string;
  expiresAt: string | null;
  cancelledAt: string | null;
  nextBillingDate: string | null;
  cancelAtPeriodEnd: boolean;
  lastPaymentAt: string | null;
  hasBillingKey: boolean;
  isActive: boolean;
}

export interface CreditTransaction {
  id: string;
  transactionType: 'purchase' | 'spend' | 'bonus' | 'refund' | 'monthly_grant';
  amount: number;
  balanceAfter: number;
  description: string;
  reference: string;
  createdAt: string;
}

export interface CreditPackage {
  id: string;
  credits: number;
  amount_krw: number;
  label: string;
  badge: string | null;
}

export interface PreparePaymentRequest {
  package_id: string;
}

export interface PreparePaymentResponse {
  order_id: string;
  amount: number;
  order_name: string;
  credits: number;
  client_key: string;
}

export interface ConfirmPaymentRequest {
  payment_key: string;
  order_id: string;
  amount: number;
}

export interface ConfirmPaymentResponse {
  credits_added: number;
  balance: number;
  message: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const billingQueryKeys = {
  wallet: () => ['/api/billing/wallet/'] as const,
  subscription: () => ['/api/billing/subscription/'] as const,
  plans: () => ['/api/billing/plans/'] as const,
  transactions: () => ['/api/billing/transactions/'] as const,
  packages: () => ['/api/billing/packages/'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

export function useCreditWallet() {
  return useQuery({
    queryKey: billingQueryKeys.wallet(),
    queryFn: async (): Promise<CreditWallet | null> => {
      try {
        const res = await AXIOS_INSTANCE.get('/api/billing/wallet/');
        return res.data;
      } catch {
        return null;
      }
    },
    staleTime: 30_000,
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: billingQueryKeys.subscription(),
    queryFn: async (): Promise<Subscription | null> => {
      try {
        const res = await AXIOS_INSTANCE.get('/api/billing/subscription/');
        return res.data?.plan ? res.data : null;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: billingQueryKeys.plans(),
    queryFn: async (): Promise<Plan[]> => {
      try {
        const res = await AXIOS_INSTANCE.get('/api/billing/plans/');
        return res.data?.results ?? res.data ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreditPackages() {
  return useQuery({
    queryKey: billingQueryKeys.packages(),
    queryFn: async (): Promise<CreditPackage[]> => {
      const res = await AXIOS_INSTANCE.get('/api/billing/packages/');
      return res.data?.packages ?? [];
    },
    staleTime: 10 * 60_000,
  });
}

export function useCreditTransactions() {
  return useQuery({
    queryKey: billingQueryKeys.transactions(),
    queryFn: async (): Promise<CreditTransaction[]> => {
      try {
        const res = await AXIOS_INSTANCE.get('/api/billing/transactions/');
        return res.data?.results ?? res.data ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function usePreparePayment() {
  return useMutation({
    mutationFn: async (req: PreparePaymentRequest): Promise<PreparePaymentResponse> => {
      const res = await AXIOS_INSTANCE.post('/api/billing/payments/prepare/', req);
      return res.data;
    },
  });
}

export function useConfirmPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: ConfirmPaymentRequest): Promise<ConfirmPaymentResponse> => {
      const res = await AXIOS_INSTANCE.post('/api/billing/payments/confirm/', req);
      return res.data;
    },
    onSuccess: () => {
      // 결제 완료 후 지갑 잔액 즉시 갱신
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.wallet() });
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.transactions() });
    },
  });
}
